import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { syncApi } from '@/api/syncApi'
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  KeySquare,
  Pencil,
  Search,
  UserPlus,
  X,
} from 'lucide-react'
import { usersApi, type ManagedUser } from '@/api/usersApi'
import { userToggleOptions } from '@/lib/usersCache'
import { userIdFromEmail } from '@/lib/userId'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortParam } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { rolesApi, type RoleListItem } from '@/api/rolesApi'
import { tenantsApi, type Tenant } from '@/api/tenantsApi'
import { Checkbox } from '@/components/ui/checkbox'
import { usePermission } from '@/lib/permissions'
import { isPlatformRole, roleAccessHint } from '@/lib/roleAccess'
import { buildCreateUserPayload } from '@/lib/userCreate'
import { useAuth } from '@/providers/AuthProvider'
import { useTenant } from '@/providers/TenantProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

// Per-user column layout. Name and the actions cell are locked: the name is how
// a row is identified, and hiding the actions would leave no way to edit or
// remove a user. See src/components/table/index.ts for the pattern.
const USERS_TABLE_ID = 'users'

const USERS_COLUMNS: TableColumnDef[] = [
  // First column (z8nrz7e46f). Header renamed from "Username"; the id stays so
  // saved column preferences survive. Order comes from this list, not prefs.
  // `sortable` columns sort SERVER-side (z8nrz7e4h6): this list is paged, so the
  // ids here are the backend's allowed sort keys (USER_SORTS in users.service).
  { id: 'username', header: 'User ID', defaultWidth: 160, sortable: true },
  { id: 'name', header: 'Name', locked: true, defaultWidth: 200, sortable: true },
  { id: 'email', header: 'Email', defaultWidth: 240, sortable: true },
  { id: 'superAdmin', header: 'Super admin', defaultWidth: 160, sortable: true },
  { id: 'tenantAccess', header: 'Tenant access', defaultWidth: 220 },
  // Lab role (Resources deck): a Supervisor approves absences and owns a team.
  { id: 'supervisor', header: 'Supervisor', defaultWidth: 130, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 120, sortable: true },
  // A flag only for now: nothing enforces it until SSO sign-in exists.
  { id: 'ssoOnly', header: 'SSO Only', defaultWidth: 120, sortable: true },
  // 4 icon buttons at 40px + gaps + px-4 padding.
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 210 },
]

export function UsersPage() {
  const cols = useTableColumns(USERS_TABLE_ID, USERS_COLUMNS)
  const qc = useQueryClient()
  const { email: currentEmail } = useAuth()
  const { isSuperAdmin } = usePermission()
  // Only a default for the Add-user dialog's tenant picker; this screen itself
  // is global and reads nothing else from the selection.
  const { selectedTenantId } = useTenant()

  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(20)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Debounce the search box, and drop back to page 1 when the query changes —
  // staying on page 5 of a narrower result set shows an empty table.
  //
  // Guarded on the values themselves rather than a "first run" ref: StrictMode
  // invokes effects twice on mount, which defeats a ref flag and lets the timer
  // fire 300ms after load, resetting skip and snapping any paging in that window
  // back to page 1. When query already equals debouncedQuery there is nothing to
  // debounce. Same shape as PatientsOverviewPage, which learned this the hard way.
  useEffect(() => {
    if (query === debouncedQuery) return
    const id = setTimeout(() => {
      setDebouncedQuery(query)
      setSkip(0)
    }, 300)
    return () => clearTimeout(id)
  }, [query, debouncedQuery])

  // The search term is part of the query key: results and `total` both change
  // with it, and sharing a key across terms would serve one search's rows while
  // another's pager was on screen.
  // A new sort is a new ordering of the whole list, so page 1 again — same
  // reasoning as a new search term.
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }
  const users = useQuery({
    queryKey: ['users', skip, take, debouncedQuery, sort],
    queryFn: () => usersApi.list({ skip, take, search: debouncedQuery, sort }),
  })
  const roles = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list })
  // Admins on this page are full-access, so these lists are complete.
  const tenantsQuery = useQuery({ queryKey: ['tenants'], queryFn: tenantsApi.list })

  const [createOpen, setCreateOpen] = useState(false)
  const [editProfile, setEditProfile] = useState<ManagedUser | null>(null)
  const [membershipsFor, setMembershipsFor] = useState<ManagedUser | null>(null)
  const [resetPasswordFor, setResetPasswordFor] = useState<ManagedUser | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Tenant ids are opaque; the access badges show names.
  const tenantName = (id: string) => tenantsQuery.data?.find((t) => t.id === id)?.name ?? id

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['users'], exact: false })

  const onErr = (e: unknown) => setError(e instanceof Error ? e.message : 'Action failed')

  const createUser = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      setCreateOpen(false)
      setError(null)
      invalidate()
    },
    onError: onErr,
  })
  // No onError: this one fails with its dialog still open, so the message is
  // rendered inside the dialog from `updateProfile.error`. Routing it to the
  // page banner as well would leave a stale message behind after Cancel.
  const updateProfile = useMutation({
    mutationFn: (i: { id: string; firstName: string; lastName: string; username?: string }) =>
      usersApi.updateProfile(i.id, {
        firstName: i.firstName,
        lastName: i.lastName,
        username: i.username,
      }),
    onSuccess: () => {
      setEditProfile(null)
      setError(null)
      invalidate()
    },
  })
  // Per-user switches move on the click (see userToggleOptions). Waiting on the
  // refetch instead made the first click look dead on a deployed environment.
  function useUserToggle<V extends { id: string }>(
    send: (v: V) => Promise<ManagedUser>,
    patchOf: (v: V) => Partial<ManagedUser>,
  ) {
    return useMutation(userToggleOptions(qc, send, patchOf, onErr))
  }
  const setSuperAdmin = useUserToggle(
    (i: { id: string; isSuperAdmin: boolean }) => usersApi.setSuperAdmin(i.id, i.isSuperAdmin),
    (i) => ({ isSuperAdmin: i.isSuperAdmin }),
  )
  const setEnabled = useUserToggle(
    (i: { id: string; enabled: boolean }) => usersApi.setEnabled(i.id, i.enabled),
    (i) => ({ enabled: i.enabled }),
  )
  const setSsoOnly = useUserToggle(
    (i: { id: string; ssoOnly: boolean }) => usersApi.setSsoOnly(i.id, i.ssoOnly),
    (i) => ({ ssoOnly: i.ssoOnly }),
  )
  const setSupervisor = useUserToggle(
    (i: { id: string; isSupervisor: boolean }) => usersApi.setSupervisor(i.id, { isSupervisor: i.isSupervisor }),
    (i) => ({ isSupervisor: i.isSupervisor }),
  )
  /** Only the row being saved is locked, not every row's switch. */
  const savingRow = (m: { isPending: boolean; variables?: { id: string } }, id: string) =>
    m.isPending && m.variables?.id === id
  // Same as updateProfile: surfaced in-dialog, not in the page banner.
  const setMemberships = useMutation({
    mutationFn: (i: { id: string; tenants: { tenantId: string; roleId?: string }[] }) =>
      usersApi.setMemberships(i.id, i.tenants),
    onSuccess: () => {
      setMembershipsFor(null)
      setError(null)
      invalidate()
    },
  })
  // Surfaced in-dialog like updateProfile. On success the dialog stays open and
  // confirms, so the admin knows the password is live before sharing it.
  const resetPassword = useMutation({
    mutationFn: (i: { id: string; temporaryPassword: string }) =>
      usersApi.resetPassword(i.id, i.temporaryPassword),
  })
  // Offline signing key (z8nrz7fh5c): a lost phone, or someone who left.
  const retireSigningKey = useMutation({
    mutationFn: (u: ManagedUser) => syncApi.retireSigningKeyFor(u.id),
    onSuccess: ({ retired }, u) =>
      window.alert(retired ? `${u.email}'s offline signing key is retired.` : `${u.email} has no offline signing key.`),
    onError: onErr,
  })

  // Global admin surface. Gated on the flag, NOT on can('users.manage'): a tenant
  // role may legitimately carry users.manage (for its own members panel), which
  // would render this page while every request behind it 403s.
  if (!isSuperAdmin) {
    return (
      <p className='text-sm text-muted-foreground'>
        User management is restricted to super admins.
      </p>
    )
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-3'>
        <p className='text-sm text-muted-foreground'>
          Manage staff accounts, their roles and access.
        </p>
        <div className='flex items-center gap-2'>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search name or email…'
              aria-label='Search users by name or email'
              className='w-64 pl-8'
            />
            {query && (
              <button
                type='button'
                onClick={() => setQuery('')}
                aria-label='Clear search'
                className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
              >
                <X className='h-4 w-4' />
              </button>
            )}
          </div>
          <Button onClick={() => setCreateOpen(true)} className='gap-2'>
            <UserPlus className='h-4 w-4' /> Add user
          </Button>
        </div>
      </div>

      {error && (
        <p className='text-sm text-destructive' role='alert'>
          {error}
        </p>
      )}

      <Card>
        <CardContent className='p-0'>
          {users.isLoading && <p className='p-4 text-sm text-muted-foreground'>Loading…</p>}
          {users.isError && (
            <p className='p-4 text-sm text-destructive'>
              Could not load users. {(users.error as Error).message}
            </p>
          )}
          {users.data && (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={def.id === 'actions' ? 'text-right' : undefined}
                      resizable={def.id !== 'actions'}
                    >
                      {def.header}
                    </ResizableTableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.data.items.map((u) => {
                  const isSelf = u.email === currentEmail
                  return (
                    <TableRow key={u.id}>
                      <ColumnCells
                        columns={cols}
                        cells={{
                          name: (
                            <TableCell className='font-medium'>
                              {u.fullName || '—'}
                              {isSelf && <Badge variant='outline' className='ml-2'>you</Badge>}
                            </TableCell>
                          ),
                          email: <TableCell className='text-muted-foreground'>{u.email}</TableCell>,
                          username: (
                            <TableCell>
                              <code className='text-xs'>{u.username ?? '—'}</code>
                            </TableCell>
                          ),
                          superAdmin: (
                            <TableCell>
                              <div className='flex items-center gap-2'>
                                <Switch
                                  checked={u.isSuperAdmin}
                                  disabled={isSelf || savingRow(setSuperAdmin, u.id)}
                                  title={
                                    isSelf
                                      ? 'You cannot change your own platform access'
                                      : 'Platform-wide access: every tenant plus the global admin screens'
                                  }
                                  onCheckedChange={(next) => {
                                    setError(null)
                                    if (
                                      !next ||
                                      window.confirm(
                                        `Give ${u.email} platform-wide access to every tenant and all admin screens?`,
                                      )
                                    ) {
                                      setSuperAdmin.mutate({ id: u.id, isSuperAdmin: next })
                                    }
                                  }}
                                />
                                {u.isSuperAdmin && <Badge>super admin</Badge>}
                              </div>
                            </TableCell>
                          ),
                          tenantAccess: (
                            <TableCell>
                              {u.isSuperAdmin ? (
                                <span
                                  className='text-xs text-muted-foreground'
                                  title='Superadmins reach every tenant regardless of membership'
                                >
                                  all tenants
                                </span>
                              ) : u.tenantRoles.length === 0 ? (
                                <span
                                  className='text-xs text-muted-foreground'
                                  title='No memberships — this user cannot act as any tenant'
                                >
                                  —
                                </span>
                              ) : (
                                <div className='flex flex-wrap items-center gap-1'>
                                  {u.tenantRoles.map((t) => (
                                    <Badge
                                      key={t.tenantId}
                                      variant={t.roleId ? 'outline' : 'secondary'}
                                      title={
                                        t.roleId
                                          ? undefined
                                          : 'Member with no role here — no permissions until one is set'
                                      }
                                    >
                                      {tenantName(t.tenantId)}
                                      {': '}
                                      {t.roleDisplayName ?? 'no role'}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                          ),
                          status: (
                            <TableCell>
                              <div className='flex items-center gap-2'>
                                <Switch
                                  checked={u.enabled}
                                  disabled={isSelf || savingRow(setEnabled, u.id)}
                                  title={isSelf ? 'You cannot deactivate yourself' : u.enabled ? 'Deactivate' : 'Reactivate'}
                                  aria-label={u.enabled ? `Deactivate ${u.email}` : `Reactivate ${u.email}`}
                                  onCheckedChange={(enabled) => {
                                    setError(null)
                                    setEnabled.mutate({ id: u.id, enabled })
                                  }}
                                />
                                {!u.enabled && <Badge variant='secondary'>deactivated</Badge>}
                              </div>
                            </TableCell>
                          ),
                          supervisor: (
                            <TableCell>
                              <Switch
                                checked={u.isSupervisor}
                                disabled={savingRow(setSupervisor, u.id)}
                                aria-label={`Supervisor for ${u.email}`}
                                title='Supervisor (lab role)'
                                onCheckedChange={(isSupervisor) => {
                                  setError(null)
                                  setSupervisor.mutate({ id: u.id, isSupervisor })
                                }}
                              />
                            </TableCell>
                          ),
                          ssoOnly: (
                            <TableCell>
                              <Switch
                                checked={u.ssoOnly}
                                disabled={savingRow(setSsoOnly, u.id)}
                                aria-label={`SSO Only for ${u.email}`}
                                title='SSO Only'
                                onCheckedChange={(ssoOnly) => {
                                  setError(null)
                                  setSsoOnly.mutate({ id: u.id, ssoOnly })
                                }}
                              />
                            </TableCell>
                          ),
                          actions: (
                            <TableCell>
                              <div className='flex items-center justify-end gap-1'>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  title='Edit name / username'
                                  onClick={() => {
                                    setError(null)
                                    setEditProfile(u)
                                  }}
                                >
                                  <Pencil className='h-4 w-4' />
                                </Button>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  title='Tenant memberships'
                                  onClick={() => {
                                    setError(null)
                                    setMembershipsFor(u)
                                  }}
                                >
                                  <Building2 className='h-4 w-4' />
                                </Button>
                                {/* Your own password changes from Profile, which asks for the
                                    current one. */}
                                {!isSelf && (
                                  <Button
                                    variant='ghost'
                                    size='icon'
                                    title='Reset password'
                                    onClick={() => {
                                      setError(null)
                                      setResetPasswordFor(u)
                                    }}
                                  >
                                    <KeyRound className='h-4 w-4' />
                                  </Button>
                                )}
                                {!isSelf && (
                                  <Button
                                    variant='ghost'
                                    size='icon'
                                    title='Retire offline signing key'
                                    disabled={retireSigningKey.isPending}
                                    onClick={() => {
                                      if (window.confirm(`Retire ${u.email}'s offline signing key? Their phone stops signing offline until they sign in again.`)) {
                                        setError(null)
                                        retireSigningKey.mutate(u)
                                      }
                                    }}
                                  >
                                    <KeySquare className='h-4 w-4' />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          ),
                        }}
                      />
                    </TableRow>
                  )
                })}
                {/* An empty table after a search reads as a broken screen. Say
                    which term found nothing, and offer the way back. */}
                {users.data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={cols.visible.length} className='py-10 text-center'>
                      {debouncedQuery ? (
                        <div className='flex flex-col items-center gap-2'>
                          <p className='text-sm text-muted-foreground'>
                            No users match “{debouncedQuery}”.
                          </p>
                          <Button variant='outline' size='sm' onClick={() => setQuery('')}>
                            Clear search
                          </Button>
                        </div>
                      ) : (
                        <p className='text-sm text-muted-foreground'>No users yet.</p>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <div className='flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground'>
              <span>
                {users.data.total > 0
                  ? `${skip + 1}–${Math.min(skip + take, users.data.total)} of ${users.data.total} users`
                  : '0 users'}
              </span>
              <div className='flex items-center gap-2'>
                <select
                  value={take}
                  onChange={e => { setTake(Number(e.target.value)); setSkip(0) }}
                  className='rounded border bg-background px-2 py-0.5 text-xs'
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <div className='flex items-center gap-1'>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip === 0}
                    onClick={() => setSkip(s => Math.max(0, s - take))}
                  >
                    <ChevronLeft className='h-3.5 w-3.5' />
                    Previous
                  </button>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip + take >= users.data.total}
                    onClick={() => setSkip(s => s + take)}
                  >
                    Next
                    <ChevronRight className='h-3.5 w-3.5' />
                  </button>
                </div>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <CreateUserDialog
          tenants={tenantsQuery.data ?? []}
          roles={roles.data ?? []}
          defaultTenantId={selectedTenantId}
          pending={createUser.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(payload) => createUser.mutate(payload)}
        />
      )}

      {editProfile && (
        <EditProfileDialog
          user={editProfile}
          pending={updateProfile.isPending}
          error={updateProfile.error instanceof Error ? updateProfile.error.message : null}
          onClose={() => {
            updateProfile.reset()
            setEditProfile(null)
          }}
          onSave={(firstName, lastName, username) =>
            updateProfile.mutate({ id: editProfile.id, firstName, lastName, username })
          }
        />
      )}

      {membershipsFor && (
        <MembershipsDialog
          user={membershipsFor}
          tenants={tenantsQuery.data ?? []}
          pending={setMemberships.isPending}
          error={setMemberships.error instanceof Error ? setMemberships.error.message : null}
          onClose={() => {
            setMemberships.reset()
            setMembershipsFor(null)
          }}
          roles={roles.data ?? []}
          onSave={(tenants) => setMemberships.mutate({ id: membershipsFor.id, tenants })}
        />
      )}

      {resetPasswordFor && (
        <ResetPasswordDialog
          user={resetPasswordFor}
          pending={resetPassword.isPending}
          done={resetPassword.isSuccess}
          error={resetPassword.error instanceof Error ? resetPassword.error.message : null}
          onClose={() => {
            resetPassword.reset()
            setResetPasswordFor(null)
          }}
          onEdit={() => {
            if (resetPassword.isError) resetPassword.reset()
          }}
          onSave={(temporaryPassword) =>
            resetPassword.mutate({ id: resetPasswordFor.id, temporaryPassword })
          }
        />
      )}
    </div>
  )
}

const NO_ROLE = '__none__'

/**
 * Tenant access is one row per tenant: a checkbox for membership plus the role
 * held THERE. The same user can be an admin in one tenant and a supervisor in
 * another, which is the whole point of the model.
 */
function MembershipsDialog({
  user,
  tenants,
  roles,
  pending,
  error,
  onClose,
  onSave,
}: {
  user: ManagedUser
  tenants: Tenant[]
  roles: RoleListItem[]
  pending: boolean
  /**
   * Save failure, rendered INSIDE the dialog. The page-level banner is not
   * enough: this dialog stays mounted on failure and its modal overlay covers
   * the banner, so the admin would only see the button flip back from "Saving…"
   * and conclude the click did nothing.
   */
  error: string | null
  onClose: () => void
  onSave: (tenants: { tenantId: string; roleId?: string }[]) => void
}) {
  // tenantId -> roleId ('' = member with no role yet). Absent key = not a member.
  const [assigned, setAssigned] = useState<Record<string, string>>(() =>
    Object.fromEntries(user.tenantRoles.map((t) => [t.tenantId, t.roleId ?? ''])),
  )

  const toggle = (tenantId: string, checked: boolean) =>
    setAssigned((prev) => {
      if (!checked) {
        const { [tenantId]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [tenantId]: prev[tenantId] ?? '' }
    })

  const setRole = (tenantId: string, roleId: string) =>
    setAssigned((prev) => ({ ...prev, [tenantId]: roleId === NO_ROLE ? '' : roleId }))

  const save = () =>
    onSave(
      Object.entries(assigned).map(([tenantId, roleId]) => ({
        tenantId,
        ...(roleId ? { roleId } : {}),
      })),
    )

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{user.email} — tenant access</DialogTitle>
          <DialogDescription>
            Pick the tenants this user belongs to and their role in each. A member with no
            role can reach the tenant but has no permissions in it. Super admins reach every
            tenant regardless of what is set here.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-2'>
          {tenants.length === 0 && (
            <p className='text-xs text-muted-foreground'>No tenants exist yet.</p>
          )}
          {tenants.map((t) => {
            const isMember = t.id in assigned
            return (
              <div key={t.id} className='flex items-center gap-3 rounded-md border px-3 py-2'>
                <Checkbox
                  id={`member-${t.id}`}
                  checked={isMember}
                  onCheckedChange={(checked) => toggle(t.id, checked === true)}
                />
                <label htmlFor={`member-${t.id}`} className='flex-1 text-sm'>
                  {t.name}
                </label>
                <Select
                  value={assigned[t.id] || NO_ROLE}
                  disabled={!isMember}
                  onValueChange={(roleId) => setRole(t.id, roleId)}
                >
                  <SelectTrigger className='h-8 w-44 text-xs'>
                    <SelectValue placeholder='No role' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ROLE}>No role</SelectItem>
                    {roles.map((r) => {
                      const hint = roleAccessHint(r)
                      return (
                        <SelectItem key={r.id} value={r.id}>
                          {r.displayName}
                          {hint && <span className='ml-1.5 text-muted-foreground'>· {hint}</span>}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            )
          })}
        </div>

        {error && (
          <p className='text-sm text-destructive' role='alert'>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={pending} onClick={save}>
            {pending ? 'Saving…' : 'Save access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Creates the account AND its access: a role in each tenant it belongs to.
 *
 * Access used to be a "Super admin" checkbox and nothing else, so an ordinary
 * account was created with membership of no tenant and a role nowhere — every
 * user row in the database had a null membership, and the operator had to know
 * to reopen the Tenant access dialog afterwards to make the account usable.
 *
 * Several tenants, not one. Staff routinely cover more than one hospital, and
 * the model has always allowed it (`TenantMembership` is per tenant and carries
 * its own role); the single picker here was the only thing that did not, which
 * made "create the account, then immediately reopen Tenant access" the normal
 * path. Same layout as MembershipsDialog on purpose — the two screens set the
 * same thing, so they should not need reading twice.
 *
 * The tenants are explicit controls rather than the one in the header switcher.
 * A role only means anything inside a tenant, and granting access in whichever
 * tenant happened to be selected is a mistake nothing on screen would show; the
 * switcher's tenant is pre-ticked as a starting point, not the decision.
 *
 * Platform-wide access is deliberately NOT here: it is not a role (it spans
 * every tenant), and it stays where it can be seen and revoked — the toggle on
 * the user's row.
 */
function CreateUserDialog({
  tenants,
  roles,
  defaultTenantId,
  pending,
  onClose,
  onSubmit,
}: {
  tenants: Tenant[]
  roles: RoleListItem[]
  /** The header switcher's tenant — a starting point, not the decision. */
  defaultTenantId: string | null
  pending: boolean
  onClose: () => void
  onSubmit: (p: {
    email: string
    firstName: string
    lastName: string
    temporaryPassword: string
    tenants?: { tenantId: string; roleId?: string }[]
  }) => void
}) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // tenantId -> roleId ('' = ticked but no role picked yet). Absent key = not a
  // member. Same shape as MembershipsDialog so the two behave identically.
  const [assigned, setAssigned] = useState<Record<string, string>>(() =>
    defaultTenantId ? { [defaultTenantId]: '' } : {},
  )
  const [password, setPassword] = useState('')

  // Trimmed here as well as server-side: the button below is gated on the same
  // values, so a name of spaces must not look like a filled-in field.
  const first = firstName.trim()
  const last = lastName.trim()

  const toggle = (tenantId: string, checked: boolean) =>
    setAssigned((prev) => {
      if (!checked) {
        const { [tenantId]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [tenantId]: prev[tenantId] ?? '' }
    })

  const setRole = (tenantId: string, roleId: string) =>
    setAssigned((prev) => ({ ...prev, [tenantId]: roleId }))

  const chosen = Object.entries(assigned)
  // Every ticked tenant needs a role BEFORE the account exists. The Tenant
  // access dialog allows a role-less membership because it is editing an
  // account that already works; creating one that way just makes a user who
  // can sign in and do nothing — the exact failure this dialog was fixed for.
  const missingRole = chosen.some(([, roleId]) => !roleId)
  // Picking the platform role anywhere makes the whole ACCOUNT a superadmin —
  // it is not scoped to the tenant it was picked in. Say so at the moment of
  // the choice; the row toggle is the only other place it is visible.
  const grantsPlatform = chosen.some(([, roleId]) => {
    const role = roles.find((r) => r.id === roleId)
    return !!role && isPlatformRole(role)
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    // Built in `buildCreateUserPayload` so the membership half is covered by a
    // test — vitest here matches `src/**/*.test.ts` only, never `.tsx`.
    onSubmit(
      buildCreateUserPayload({
        email,
        firstName,
        lastName,
        temporaryPassword: password,
        memberships: chosen.map(([tenantId, roleId]) => ({ tenantId, roleId })),
      }),
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Creates the account and its first access. No email is sent, so pass the password on
            yourself — they sign in with it as it is, and can change it from their profile.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='u-email'>Email</Label>
            <Input id='u-email' type='email' required value={email} onChange={(e) => setEmail(e.target.value)} />
            {userIdFromEmail(email) && (
              <p className='text-xs text-muted-foreground' data-testid='user-id-preview'>
                User ID: <code>{userIdFromEmail(email)}</code> — taken from the email; numbered if
                already in use.
              </p>
            )}
          </div>
          {/* Two boxes rather than one "Name": the record stores the parts,
              and a single field would have to be split back apart to fill an
              edit form — which is what "Ada de la Cruz" makes a guess. */}
          <div className='grid grid-cols-2 gap-3'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='u-first'>First name</Label>
              <Input
                id='u-first'
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='u-last'>Last name</Label>
              <Input
                id='u-last'
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          {/* Access: tick every tenant the account belongs to and set the role
              it holds THERE. A role is held IN a tenant, so neither half means
              anything without the other. */}
          <div className='flex flex-col gap-1.5'>
            <Label>Tenant access</Label>
            <div className='flex max-h-56 flex-col gap-2 overflow-y-auto'>
              {tenants.length === 0 && (
                <p className='text-xs text-muted-foreground'>No tenants exist yet.</p>
              )}
              {tenants.map((t) => {
                const isMember = t.id in assigned
                return (
                  <div key={t.id} className='flex items-center gap-3 rounded-md border px-3 py-2'>
                    <Checkbox
                      id={`create-member-${t.id}`}
                      checked={isMember}
                      onCheckedChange={(checked) => toggle(t.id, checked === true)}
                    />
                    <label htmlFor={`create-member-${t.id}`} className='flex-1 text-sm'>
                      {t.name}
                    </label>
                    <Select
                      value={assigned[t.id] || ''}
                      disabled={!isMember}
                      onValueChange={(roleId) => setRole(t.id, roleId)}
                    >
                      <SelectTrigger className='h-8 w-44 text-xs'>
                        <SelectValue placeholder='Select role…' />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => {
                          const hint = roleAccessHint(r)
                          return (
                            <SelectItem key={r.id} value={r.id}>
                              {r.displayName}
                              {hint && (
                                <span className='ml-1.5 text-muted-foreground'>· {hint}</span>
                              )}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )
              })}
            </div>
            {grantsPlatform && (
              <p className='text-xs text-muted-foreground'>
                Super Admin makes this a platform account: every tenant, plus the global admin
                screens — not just the tenant it is picked in. Revoke it from the Super admin
                toggle on the user's row.
              </p>
            )}
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='u-pass'>Password</Label>
            <PasswordInput
              id='u-pass'
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            {/* Tenants and roles are part of the decision, not an afterthought:
                an account created without them can sign in and do nothing. */}
            <Button
              type='submit'
              disabled={
                pending ||
                !email.trim() ||
                !first ||
                !last ||
                chosen.length === 0 ||
                missingRole ||
                password.length < 8
              }
            >
              {pending ? 'Creating…' : 'Create user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditProfileDialog({
  user,
  pending,
  error,
  onClose,
  onSave,
}: {
  user: ManagedUser
  pending: boolean
  /** Save failure, rendered INSIDE the dialog — see MembershipsDialog. */
  error: string | null
  onClose: () => void
  onSave: (firstName: string, lastName: string, username: string) => void
}) {
  const [firstName, setFirstName] = useState(user.firstName ?? '')
  const [lastName, setLastName] = useState(user.lastName ?? '')
  const [username, setUsername] = useState(user.username ?? '')

  const first = firstName.trim()
  const last = lastName.trim()

  function submit(e: FormEvent) {
    e.preventDefault()
    onSave(first, last, username.trim())
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Edit {user.email}</DialogTitle>
          <DialogDescription>Update the user's name and User ID.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className='flex flex-col gap-4'>
          <div className='grid grid-cols-2 gap-3'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='ep-first'>First name</Label>
              <Input
                id='ep-first'
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='ep-last'>Last name</Label>
              <Input
                id='ep-last'
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          {/* An account predating the name parts arrives here with neither, and
              the server refuses ANY save until both are given — including a
              save that only meant to change the username. Say so up front
              rather than letting the 400 explain it. */}
          {!user.firstName || !user.lastName ? (
            <p className='text-xs text-muted-foreground'>
              This account has no name on record. Both are now required, so saving needs them
              filled in.
            </p>
          ) : null}
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='ep-username'>User ID</Label>
            {/* No minimum length: a User ID is the email's local part, and
                `jo@x.com` is a real address. */}
            <Input
              id='ep-username'
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          {error && (
            <p className='text-sm text-destructive' role='alert'>
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !first || !last}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ResetPasswordDialog({
  user,
  pending,
  done,
  error,
  onClose,
  onEdit,
  onSave,
}: {
  user: ManagedUser
  pending: boolean
  done: boolean
  /** Save failure, rendered INSIDE the dialog. See MembershipsDialog. */
  error: string | null
  onClose: () => void
  /** Clears a stale error once the admin starts fixing the password. */
  onEdit: () => void
  onSave: (temporaryPassword: string) => void
}) {
  const [password, setPassword] = useState('')
  const who = user.fullName || user.email

  function submit(e: FormEvent) {
    e.preventDefault()
    onSave(password)
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Reset password for {who}</DialogTitle>
          <DialogDescription>
            Share the new password with the user. They sign in with it as it is, and can change
            it from their profile.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <>
            <p className='text-sm' role='status'>
              The password is reset. {who} can now sign in with it.
            </p>
            <DialogFooter>
              <Button type='button' onClick={onClose}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={submit} className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='rp-pass'>New password</Label>
              <PasswordInput
                id='rp-pass'
                required
                minLength={8}
                autoComplete='new-password'
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  onEdit()
                }}
              />
              <p className='text-xs text-muted-foreground'>
                At least 8 characters, with an uppercase letter, a lowercase letter, and a number.
              </p>
            </div>
            {error && (
              <p className='text-sm text-destructive' role='alert'>
                {error}
              </p>
            )}
            <DialogFooter>
              <Button type='button' variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button type='submit' disabled={pending || password.length < 8}>
                {pending ? 'Resetting…' : 'Reset password'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
